/**
 * Notification wire contracts (ADR-0009).
 *
 * Browser-safe Zod only — no SDK imports. Exported through schemas/index.ts.
 * All three delivery channels (web_push, email, expo_push) and the alert engine
 * share these shapes.
 */
import { z } from 'zod';
// ── Channel / device enums ─────────────────────────────────────────────────────
/**
 * Delivery channel kinds (ADR-0009 §5 seam).
 *
 * `wonderpush_push` (ADR-0019) is the single managed push channel that replaces
 * the first-party `web_push` + `expo_push` adapters. The legacy kinds are kept
 * in the enum during the deprecation window so historical `notification_log`
 * rows (channels_attempted) still validate; new sends only ever use
 * `wonderpush_push` or `email`.
 */
export const notificationChannelKindSchema = z.enum([
    'wonderpush_push',
    'email',
    // Deprecated (ADR-0019): first-party push adapters, replaced by wonderpush_push.
    'web_push',
    'expo_push',
]);
// ── WonderPush subscription marker (ADR-0019) ─────────────────────────────────
/**
 * Client → server marker that the device/browser has an active WonderPush
 * subscription for the signed-in user (consent + browser permission granted from
 * the installed PWA).
 * The dispatcher reads this marker to decide "has push" for the email-fallback
 * policy — WonderPush itself owns device lifecycle, so we keep only a userId-
 * bound boolean rather than per-device rows (POST /me/wonderpush/subscription).
 */
export const wonderPushSubscriptionStateSchema = z.object({
    /** True when the client has an active push subscription; false on revoke/sign-out. */
    subscribed: z.boolean(),
});
export const devicePlatformSchema = z.enum(['ios', 'android']);
// ── Push-token registration (POST /me/push-tokens) ────────────────────────────
export const registerPushTokenSchema = z.object({
    /** Expo push token string (e.g. "ExponentPushToken[xxx]"). */
    token: z.string().min(1),
    platform: devicePlatformSchema,
    /** Optional device identifier for replace-on-reinstall deduplication. */
    deviceId: z.string().optional(),
});
export const pushTokenViewSchema = z.object({
    id: z.string().uuid(),
    platform: devicePlatformSchema,
    deviceId: z.string().nullable(),
    createdAt: z.string().datetime(),
    lastUsedAt: z.string().datetime().nullable(),
});
// ── Web-push subscription (POST /me/push-subscriptions) ───────────────────────
export const webPushKeysSchema = z.object({
    p256dh: z.string().min(1),
    auth: z.string().min(1),
});
export const webPushSubscriptionSchema = z.object({
    /** W3C Push API endpoint URL. */
    endpoint: z.string().url(),
    keys: webPushKeysSchema,
    /** RFC-3339 expiry; null/absent when no server-set expiry. */
    expirationTime: z.number().nullable().optional(),
});
// ── Notification payload (channel-agnostic) ───────────────────────────────────
// API contract touch (ISSUE-066): push kinds now cover ADR-0019 §3 cases 2-4.
export const notificationKindSchema = z.enum([
    'goal_recovery',
    'weekly_review',
    'trial_recap',
    'trial_ended',
]);
export const notificationActionSchema = z.object({
    /** Button / CTA label shown to the user. */
    label: z.string().max(60),
    /** Natural-language prefill text sent to the extraction pipeline on tap (alerts only). */
    prefillText: z.string().max(120).optional(),
    /** Platform-neutral route name (e.g. 'log'). */
    route: z.string(),
});
export const quickLogHelperSchema = z.object({
    /** Short one-tap option label. */
    label: z.string().max(60),
    /** Natural-language prefill text for the extraction pipeline. */
    prefillText: z.string().max(120),
});
export const notificationPayloadSchema = z.object({
    /** Correlates the push back to notification_log.id. */
    alertId: z.string().uuid(),
    /**
     * ADR-0033: when this push mirrors a Today nudge, the nudge's deterministic
     * id (`nudge:{date}:{kind}`). The channel sets WonderPush `alert.tag` to it
     * so an in-app dismissal can clear the delivered push (silent
     * `closeNotifications` receive-action), and the tap handler highlights that
     * nudge card on Today. Absent for non-nudge pushes.
     */
    nudgeId: z.string().optional(),
    kind: notificationKindSchema,
    /** Which macro the gap is about (e.g. 'protein', 'fibre'). Null for non-macro kinds. */
    macro: z.string().nullable(),
    /** Shortfall in grams (display-only, deterministic from computeDaySummary.remaining). */
    gapG: z.number().nullable(),
    /** Push notification title (≤ 60 chars). */
    title: z.string().max(60),
    /** Push notification body (≤ 120 chars). */
    body: z.string().max(120),
    /** Primary CTA (deep-links to the destination surface). */
    action: notificationActionSchema,
    /** Quick-log option helpers surfaced below alert bodies. Empty for non-alert pushes. */
    quickLogHelpers: z.array(quickLogHelperSchema).max(3).default([]),
    /** ISSUE-058: resource link shown in every alert footer. */
    resourceUrl: z.string().url(),
    createdAt: z.string().datetime(),
    /**
     * ADR-0029 / T16.2 — "build the rest of today" premium upsell CTA.
     * Present on goal_recovery alerts. Clients (T16.3) render this alongside the
     * primary action as a secondary button; premium users tap to `POST /me/plan/fill`.
     * Absent for non-goal-recovery notification kinds.
     */
    buildRestCta: z
        .object({
        /** YYYY-MM-DD of the alert day (used by the client to call POST /me/plan/fill). */
        date: z.string().date(),
        /** Button label (translated on the client; this is the en default). */
        label: z.string(),
        /** Platform-neutral route name. */
        route: z.literal('plan/fill'),
    })
        .optional(),
});
export const notificationDeepLinkSchema = z.object({
    /** Platform-neutral route name (e.g. 'log'). */
    route: z.string(),
    /** Query params forwarded to the log surface (prefill, alertId, etc.). */
    params: z.record(z.string(), z.string()),
});
// ── Notification preferences (GET / PUT /me/notification-preferences) ─────────
/**
 * Premium-only custom alert thresholds (ADR-0012 §3 `configurable_alerts`).
 * The protein-gap alert fires for all users; these fields tune its sensitivity
 * and cap. Free users may not set this field (402 PaymentRequired).
 */
export const alertCustomThresholdsSchema = z.object({
    /** Minimum shortfall as a fraction of target to trigger an alert. Default: 0.15. */
    shortfallPctOfTarget: z.number().min(0.05).max(0.5).optional(),
    /** Maximum alerts dispatched per user per day across all macros. Default: 2. */
    maxAlertsPerDay: z.number().int().min(1).max(10).optional(),
});
export const notificationPreferenceSchema = z.object({
    /** When true, no alerts are dispatched for this user (globally silenced). */
    globalMuted: z.boolean().default(false),
    /** Macros opted-out of alerts (e.g. ['protein'] means protein alerts suppressed). */
    mutedMacros: z.array(z.string()).default([]),
    /** Notification kinds opted out of entirely (e.g. weekly_review). */
    mutedKinds: z.array(notificationKindSchema).default([]),
    /**
     * When true (default), the dispatcher falls back to email when the user has
     * no healthy push targets. When false, zero-push users receive nothing.
     */
    emailFallback: z.boolean().default(true),
    /**
     * Premium-only: custom alert thresholds (ADR-0012 §3 `configurable_alerts`).
     * Absent for free users; the dispatcher applies ALERT_THRESHOLDS defaults.
     */
    customThresholds: alertCustomThresholdsSchema.optional(),
});
// ── Threshold constants (ISSUE-033 AC / ISSUE-210 tuning knob) ────────────────
export const ALERT_THRESHOLDS = {
    /** Minimum projected shortfall as a fraction of target to trigger an alert. */
    shortfallPctOfTarget: 0.15,
    /** Maximum alerts dispatched per user per day across all macros. */
    maxAlertsPerDay: 2,
};
/**
 * ADR-0033 §1: the Today nudge (protein_gap/fibre_gap) never fires before this
 * local hour, even when the shortfall already breaches ALERT_THRESHOLDS — a
 * nudge only makes sense once the day is mostly spent. Same client-supplied
 * timezone trust deferral as the 16:00 UTC alert sweep (ADR-0003 §M4).
 */
export const NUDGE_THRESHOLDS = {
    timeGateHour: 15,
};
const NUDGE_SUGGESTION_BUCKET_THRESHOLD = {
    protein_gap: 20,
    fibre_gap: 8,
};
/**
 * ADR-0033 §1/T31.3: the food-suggestion clause is a client catalog string
 * keyed by macro + gap bucket ("small"/"large") — this resolves the i18n key
 * only, so the mobile nudge card (react-i18next `t()`) and the T31.3 push
 * body (server `tl(locale, key, opts)`) render from the SAME bucket rule and
 * the SAME catalog entries. Null for `slot_bridge` (T32 owns that copy) or a
 * null gap.
 */
export function nudgeSuggestionKey(kind, gapG) {
    if (kind === 'coaching_ready')
        return 'today:nudge.coaching_ready';
    if (kind !== 'protein_gap' && kind !== 'fibre_gap')
        return null;
    if (gapG === null)
        return null;
    const rounded = Math.max(0, Math.round(gapG));
    const bucket = rounded < NUDGE_SUGGESTION_BUCKET_THRESHOLD[kind] ? 'Small' : 'Large';
    const macroKey = kind === 'protein_gap' ? 'protein' : 'fibre';
    return `today:nudge.suggestion.${macroKey}${bucket}`;
}
/** Banned copy words (ADR-0009 §4 ED-safety). Any alert body/title matching
 *  one of these words is dropped rather than sent. */
export const ALERT_BANNED_WORDS = [
    'bad',
    'cheat',
    'ruined',
    'failed',
    'behind',
    'guilt',
    'lazy',
    'weak',
    'terrible',
    'horrible',
    'disgusting',
];
//# sourceMappingURL=notifications.js.map